import { useState, useEffect } from 'react';
import {
  Box, Button, Stack, TextField, MenuItem, Typography, CircularProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Calculate as CalcIcon, Save as SaveIcon } from '@mui/icons-material';
import { useBeneficiosStore } from '../store/beneficiosStore';
import { PeriodoService } from '../../periodos/services/periodoService';
import type { PeriodoPlanilla } from '../../periodos/types/periodo';

export default function ProvisionesPanel() {
  const { provisionResultados, provisiones, loading, calcularProvisiones, grabarProvisiones, loadProvisiones } = useBeneficiosStore();
  const [periodos, setPeriodos] = useState<PeriodoPlanilla[]>([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState<number>(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    PeriodoService.getAll().then(setPeriodos);
  }, []);

  const handleCalcular = () => {
    if (selectedPeriodo > 0) calcularProvisiones(selectedPeriodo);
  };

  const handleGrabar = () => {
    if (selectedPeriodo > 0) grabarProvisiones(selectedPeriodo);
  };

  const handleLoadGrabadas = () => {
    if (selectedPeriodo > 0) loadProvisiones(selectedPeriodo);
  };

  const calculoColumns: GridColDef[] = [
    { field: 'nombreTrabajador', headerName: 'Trabajador', flex: 1, minWidth: 150 },
    ...(!isMobile ? [
      { field: 'remuneracionComputable', headerName: 'Rem. Comp.', width: 130, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'provisionCTS', headerName: 'Prov. CTS', width: 120, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'provisionGratificacion', headerName: 'Prov. Grat.', width: 120, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'provisionVacaciones', headerName: 'Prov. Vac.', width: 120, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'provisionBonifExtraordinaria', headerName: 'Prov. Bonif.', width: 120, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
    ] : []),
    { field: 'total', headerName: 'Total', width: isMobile ? 110 : 130, type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2),
      cellClassName: 'font-bold' },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Periodo" value={selectedPeriodo}
          onChange={(e) => setSelectedPeriodo(Number(e.target.value))}
          sx={{ width: { xs: '100%', sm: 250 } }}
          fullWidth={isMobile}
        >
          <MenuItem value={0}>Seleccione periodo...</MenuItem>
          {periodos.map((p) => (
            <MenuItem key={p.idPeriodo} value={p.idPeriodo}>
              {p.anio}-{String(p.mes).padStart(2, '0')} ({p.estado})
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <CalcIcon />}
            onClick={handleCalcular} disabled={loading || selectedPeriodo === 0} size={isMobile ? 'small' : 'medium'}>
            {isMobile ? 'Calcular' : 'Calcular Provisiones'}
          </Button>
          {provisionResultados.length > 0 && (
            <Button variant="outlined" color="success" startIcon={<SaveIcon />}
              onClick={handleGrabar} disabled={loading} size={isMobile ? 'small' : 'medium'}>
              Grabar
            </Button>
          )}
          <Button variant="text" onClick={handleLoadGrabadas} disabled={selectedPeriodo === 0} size={isMobile ? 'small' : 'medium'}>
            Ver Grabadas
          </Button>
        </Stack>
      </Stack>

      {provisionResultados.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Provisiones Mensuales Calculadas
          </Typography>
          <DataGrid
            rows={provisionResultados}
            columns={calculoColumns}
            getRowId={(row) => row.idTrabajador}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} sx={{ mt: 1 }}>
            <Typography variant="body2">CTS: S/ {provisionResultados.reduce((s, r) => s + r.provisionCTS, 0).toFixed(2)}</Typography>
            <Typography variant="body2">Grat: S/ {provisionResultados.reduce((s, r) => s + r.provisionGratificacion, 0).toFixed(2)}</Typography>
            <Typography variant="body2">Vac: S/ {provisionResultados.reduce((s, r) => s + r.provisionVacaciones, 0).toFixed(2)}</Typography>
            <Typography variant="body2" fontWeight={600}>
              Total: S/ {provisionResultados.reduce((s, r) => s + r.total, 0).toFixed(2)}
            </Typography>
          </Stack>
        </>
      )}

      {provisiones.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Provisiones Grabadas
          </Typography>
          <DataGrid
            rows={provisiones}
            columns={[
              ...calculoColumns.map(c => ({ ...c, field: c.field === 'nombreTrabajador' ? 'idTrabajador' : c.field })),
              { field: 'fechaCalculo', headerName: 'Fecha', width: 130,
                valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
            ]}
            getRowId={(row) => row.idProvision}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </Box>
      )}
    </Box>
  );
}
